'use client'
import * as React from 'react'
import { useSearchParams } from 'next/navigation'
import { ArrowRight, Check, Loader2, CheckCircle2, ShieldCheck } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { TurnstileWidget, useTurnstileSiteKey } from '@/lib/turnstile'
import {
  GAP_CATALOG,
  CATEGORIES,
  gapIdsFromRawChecks,
  computePlan,
  type GapId,
  type Plan,
} from '@/lib/recommendationsEngine'

// Prioritised 90-day UGC rollout plan. Takes gaps (pre-filled from the
// UGC audit / AI Visibility Check via ?gaps=<raw-check-ids>, or picked
// manually) plus category + AOV + monthly sessions, and sequences the
// gaps into three 30-day phases mapped to real Idukki modules. Output
// is a priority ORDER, never an invented revenue number, per the
// content-integrity rule in CLAUDE.md.

function moneyLabel(n: number): string {
  if (n >= 1000) return '$' + Math.round(n / 1000) + 'K'
  return '$' + n
}

export function RecommendationsEngine() {
  const params = useSearchParams()

  const initialGapIds = React.useMemo<GapId[]>(() => {
    const raw = params.get('gaps')
    if (!raw) return []
    return gapIdsFromRawChecks(raw.split(',').map((s) => s.trim()).filter(Boolean))
  }, [params])

  const initialUrl = params.get('url') || ''

  const [selected, setSelected] = React.useState<Set<GapId>>(
    () => new Set(initialGapIds.length ? initialGapIds : GAP_CATALOG.map((g) => g.id)),
  )
  const [categoryId, setCategoryId] = React.useState(CATEGORIES[0].id)
  const [aov, setAov] = React.useState(60)
  const [sessions, setSessions] = React.useState(10_000)
  const [plan, setPlan] = React.useState<Plan | null>(null)

  const toggle = (id: GapId) => {
    setSelected((s) => {
      const next = new Set(s)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
    setPlan(null)
  }

  function buildPlan(e: React.FormEvent) {
    e.preventDefault()
    setPlan(
      computePlan({
        gapIds: Array.from(selected),
        categoryId,
        aov,
        monthlySessions: sessions,
      }),
    )
  }

  return (
    <div className="grid gap-8 lg:grid-cols-[1fr_380px] lg:items-start">
      <div>
        {initialUrl && (
          <p className="mb-4 rounded-xl border border-primary/30 bg-primary/5 px-4 py-3 text-sm text-foreground">
            Pre-filled from your audit of <span className="font-semibold">{initialUrl}</span>. Adjust anything below.
          </p>
        )}
        <form onSubmit={buildPlan} className="rounded-2xl border bg-card p-6">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            1. Which gaps apply?
          </p>
          <ul className="mt-3 space-y-2">
            {GAP_CATALOG.map((g) => {
              const on = selected.has(g.id)
              return (
                <li key={g.id}>
                  <button
                    type="button"
                    onClick={() => toggle(g.id)}
                    aria-pressed={on}
                    className={`flex w-full items-start gap-3 rounded-xl border p-3.5 text-left text-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                      on ? 'border-primary/40 bg-primary/5' : 'bg-card hover:border-primary/30'
                    }`}
                  >
                    <span
                      className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border transition ${
                        on ? 'border-primary bg-primary text-primary-foreground' : 'border-muted-foreground/40'
                      }`}
                    >
                      {on && <Check className="h-3.5 w-3.5" />}
                    </span>
                    <span>
                      <span className={on ? 'text-foreground' : 'text-muted-foreground'}>{g.label}</span>
                      <span className="mt-0.5 block text-xs text-muted-foreground">{g.detail}</span>
                    </span>
                  </button>
                </li>
              )
            })}
          </ul>

          <p className="mt-6 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            2. Your business
          </p>
          <div className="mt-3 grid gap-4 sm:grid-cols-3">
            <label className="block text-sm">
              <span className="font-medium text-foreground">Category</span>
              <select
                value={categoryId}
                onChange={(e) => { setCategoryId(e.target.value); setPlan(null) }}
                className="mt-1.5 h-11 w-full rounded-xl border border-input bg-secondary/40 px-3 text-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                {CATEGORIES.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </label>
            <label className="block text-sm">
              <span className="font-medium text-foreground">Average order value</span>
              <div className="mt-2.5 flex items-center gap-2.5">
                <input
                  type="range"
                  min={10}
                  max={500}
                  step={5}
                  value={aov}
                  onChange={(e) => { setAov(Number(e.target.value)); setPlan(null) }}
                  aria-label="Average order value"
                  className="h-2 w-full cursor-pointer rounded-full accent-[hsl(var(--primary))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                />
                <span className="w-12 shrink-0 text-right text-sm font-semibold tabular-nums text-foreground">{moneyLabel(aov)}</span>
              </div>
            </label>
            <label className="block text-sm">
              <span className="font-medium text-foreground">Monthly sessions</span>
              <div className="mt-2.5 flex items-center gap-2.5">
                <input
                  type="range"
                  min={1000}
                  max={500_000}
                  step={1000}
                  value={sessions}
                  onChange={(e) => { setSessions(Number(e.target.value)); setPlan(null) }}
                  aria-label="Monthly sessions"
                  className="h-2 w-full cursor-pointer rounded-full accent-[hsl(var(--primary))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                />
                <span className="w-12 shrink-0 text-right text-sm font-semibold tabular-nums text-foreground">{moneyLabel(sessions)}</span>
              </div>
            </label>
          </div>

          <button
            type="submit"
            disabled={selected.size === 0}
            className="mt-6 inline-flex h-12 w-full items-center justify-center gap-1.5 rounded-xl bg-primary px-6 text-sm font-semibold text-primary-foreground transition hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-60"
          >
            Build my 90-day plan <ArrowRight className="h-4 w-4" />
          </button>
        </form>

        {plan && (
          <div className="mt-8 space-y-6">
            {plan.phases.map((ph, i) => (
              <div
                key={ph.phase}
                className="reco-rise rounded-2xl border bg-card p-6"
                style={{ animationDelay: `${i * 70}ms` }}
              >
                <div className="flex items-baseline justify-between gap-3">
                  <h3 className="font-display text-lg font-semibold">{ph.title}</h3>
                  <span className="text-xs uppercase tracking-[0.14em] text-muted-foreground">Days {ph.window}</span>
                </div>
                <ul className="mt-4 space-y-3">
                  {ph.items.map((it) => (
                    <li key={it.id} className="rounded-xl bg-secondary/40 p-4 transition hover:bg-secondary/60">
                      <div className="flex items-start justify-between gap-3">
                        <span className="text-sm font-semibold text-foreground">{it.label}</span>
                        <span className="whitespace-nowrap rounded-full border border-dashed border-primary/40 bg-primary/10 px-2.5 py-1 text-[11px] font-semibold text-primary">
                          priority {it.priority}
                        </span>
                      </div>
                      <p className="mt-1.5 text-sm text-muted-foreground">{it.action}</p>
                      <p className="mt-1 text-xs font-medium uppercase tracking-[0.1em] text-muted-foreground/70">
                        {it.module}
                      </p>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
            <p className="text-xs text-muted-foreground">
              Priority is Idukki&apos;s own sequencing judgement, weighted by your traffic and AOV inputs, not a
              projected revenue figure.
            </p>
          </div>
        )}
      </div>

      <aside className="lg:sticky lg:top-24">
        <PlanEmailGate plan={plan} categoryId={categoryId} aov={aov} sessions={sessions} />
      </aside>

      <style>{`
        .reco-rise { animation: recoRise 320ms cubic-bezier(.2,.9,.25,1) both; }
        @keyframes recoRise { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        @media (prefers-reduced-motion: reduce) {
          .reco-rise { animation: none; }
        }
      `}</style>
    </div>
  )
}

function PlanEmailGate({ plan, categoryId, aov, sessions }: { plan: Plan | null; categoryId: string; aov: number; sessions: number }) {
  const [email, setEmail] = React.useState('')
  const [state, setState] = React.useState<'idle' | 'sending' | 'ok' | 'error'>('idle')
  const [error, setError] = React.useState('')
  const [turnstileToken, setTurnstileToken] = React.useState('')
  const turnstileSiteKey = useTurnstileSiteKey()

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!plan) return
    setState('sending')
    setError('')
    const summary = plan.flat
      .map((it) => `[Days ${it.phase === 1 ? '0-30' : it.phase === 2 ? '30-60' : '60-90'}] ${it.label} → ${it.module}`)
      .join('\n')
    try {
      const res = await fetch('/api/leads', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          email,
          intent: 'try-tool',
          source: '/tools/recommendations-engine',
          toolSlug: 'recommendations-engine',
          message: `Recommendations engine plan — category: ${categoryId}; AOV: $${aov}; monthly sessions: ${sessions}.\n\n${summary}`,
          'cf-turnstile-response': turnstileToken,
        }),
        signal: AbortSignal.timeout(15_000),
      })
      const json = await res.json().catch(() => ({}))
      if (!res.ok || !json?.ok) {
        setState('error')
        setError(json?.error || 'Something went wrong. Try again or email hi@idukki.io.')
        return
      }
      setState('ok')
    } catch {
      setState('error')
      setError('Network error. Try again or email hi@idukki.io.')
    }
  }

  return (
    <div className="rounded-2xl border bg-card p-6">
      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Your plan</p>
      {!plan ? (
        <p className="mt-2 text-sm text-muted-foreground">
          Pick your gaps and business size, then build your plan. It shows in full on this page, free, no signup.
        </p>
      ) : state === 'ok' ? (
        <div className="mt-3 text-center">
          <CheckCircle2 className="mx-auto h-7 w-7 text-primary" />
          <p className="mt-2 font-display text-base font-semibold">Sent.</p>
          <p className="mt-1 text-sm text-muted-foreground">A real human will reply within one business day to help you scope it.</p>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="mt-3">
          <p className="text-sm text-muted-foreground">
            {plan.flat.length} fixes sequenced across {plan.phases.length} phases. Email yourself a copy, or come back
            to this page anytime.
          </p>
          <label className="mt-3 block text-sm">
            <span className="font-medium text-foreground">Work email</span>
            <Input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@brand.com"
              className="mt-1.5"
            />
          </label>
          {turnstileSiteKey && (
            <div className="mt-3">
              <p className="mb-1.5 inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
                <ShieldCheck className="h-3 w-3" /> Cloudflare bot-protection
              </p>
              <TurnstileWidget size="normal" onToken={setTurnstileToken} onExpire={() => setTurnstileToken('')} />
            </div>
          )}
          <button
            type="submit"
            disabled={state === 'sending' || (Boolean(turnstileSiteKey) && !turnstileToken)}
            className="mt-3 inline-flex w-full items-center justify-center gap-1.5 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-60"
          >
            {state === 'sending' ? (<><Loader2 className="h-4 w-4 animate-spin" /> Sending</>) : 'Email me this plan'}
          </button>
          {state === 'error' && error && <p className="mt-2 text-xs text-destructive">{error}</p>}
        </form>
      )}
    </div>
  )
}
